/**
 * Constructor de formularios
 * -----------------------------------------------------------------------------
 * Genera el formulario de crear o editar una entidad a partir de un esquema, y
 * lo parte en pasos para no soltar treinta preguntas de golpe.
 *
 *   const form = CompanyTheme.form('#form', {
 *     steps: [
 *       { title: 'Información general', columns: 2, fields: [
 *         { name: 'nombre', label: 'Nombre del lote', required: true },
 *         { name: 'cultivo_id', label: 'Cultivo', type: 'select', required: true,
 *           options: [{ value: 1, label: 'Café' }] },
 *         { name: 'valor', label: 'Valor', type: 'currency', currency: 'COP' },
 *         { name: 'obs', label: 'Observaciones', type: 'textarea', wide: true },
 *       ] },
 *       { title: 'Zocas', fields: [ … ] },
 *     ],
 *     values: registro,          // presente = edición
 *     submitText: 'Crear lote',
 *   });
 *
 *   form.getValues();  form.setValues({…});  form.validate();
 *
 * Eventos sobre el contenedor: `submit.company.form` con `{ values }` y
 * `stepChanged.company.form` con `{ step, total }`.
 *
 * El theme NO sabe qué hacer con los datos: no envía nada ni conoce endpoints.
 * Eso lo decide la aplicación al escuchar `submit`.
 *
 * El markup que genera es el mismo que el documentado para plantillas, así que
 * un proyecto puede pintar el formulario en Twig y este constructor solo para
 * las pantallas que se arman en cliente.
 */

import { formES, cardES } from './locale-es.js';
import { confirm } from './dialog.js';
import { normalizeText } from './normalize-text.js';
import { upgradeFloatingLabels, pinNativeDateLabels } from './floating-labels.js';
import { setupPickers } from './pickers.js';
import { setupMonthPickers } from './month-picker.js';
import { setupPhoneInputs } from './phone-input.js';
import { setupCurrencyInputs } from './currency-input.js';
import { setupCardInputs } from './card-input.js';
import { setupFileInputs } from './file-input.js';
import { initMdbOn, refreshMdbOn } from './mdb-init.js';
import { setupRemoteSelects } from './remote-select.js';

let uid = 0;

// MDB llega desde `index.js` para no importarlo aquí y crear un ciclo.
let mdbComponents = null;

/**
 * Recibe los componentes de MDB. Lo llama `autoInit()`.
 */
export function setFormComponents(components = {}) {
  mdbComponents = components;
}

/** Campos que el theme sabe construir. */
const TEXT_LIKE = ['text', 'email', 'tel', 'number', 'password', 'url'];

/**
 * El control real de un campo.
 *
 * No vale con `querySelector('input, select, textarea')`: eso devuelve el
 * primero en orden de documento, y MDB mete su input de adorno DELANTE del
 * `<select>` al inicializarlo. Leer ese input da el texto que se ve («Café») en
 * vez del valor («1»), y escribirlo no cambia nada.
 */
function controlOf(wrapper) {
  const nodes = wrapper.querySelectorAll('input, select, textarea');

  for (let i = 0; i < nodes.length; i += 1) {
    const node = nodes[i];
    if (node.classList.contains('select-input')) continue; // el falso de MDB
    if (node.type === 'hidden') continue; // el valor crudo de moneda y teléfono
    return node;
  }

  return nodes[0] || null;
}

/**
 * La instancia de un componente propio del theme dentro de un campo.
 *
 * Se busca en el `[data-field]` Y en lo que cuelgue de él: los componentes se
 * enganchan al `.form-outline`, que es un hijo del envoltorio del campo, no el
 * envoltorio mismo. Buscar solo en el nivel de arriba no encuentra ninguno.
 *
 * @param {HTMLElement} wrapper
 * @param {string} key `__companyCurrency`, `__companyPhone`, `__companyFile`…
 */
function componentIn(wrapper, key) {
  if (!wrapper) return null;
  if (wrapper[key]) return wrapper[key];

  const nodes = wrapper.querySelectorAll('*');
  for (let i = 0; i < nodes.length; i += 1) {
    if (nodes[i][key]) return nodes[i][key];
  }

  return null;
}

function el(tag, className, text) {
  const node = document.createElement(tag);
  if (className) node.className = className;
  if (text != null) node.textContent = text;
  return node;
}

class Form {
  constructor(container, options = {}) {
    this.container = typeof container === 'string' ? document.querySelector(container) : container;
    if (!this.container) throw new Error('company-theme: contenedor de formulario no encontrado');

    this.steps = options.steps || [];
    this.options = options;
    this.current = 0;
    this.visited = new Set([0]);

    uid += 1;
    this.uid = uid;

    // Guardando: mientras esté puesto no se manda otra vez ni se cancela.
    this.busy = false;
    this.spinner = null;

    this._build();
    if (options.values) this.setValues(options.values);

    // Con qué valores nació. Es contra esto —y no contra el vacío— contra lo
    // que se mide si hay algo escrito y a lo que se vuelve al limpiar: en modo
    // edición el formulario arranca lleno, y tratar eso como «tocado» haría
    // preguntar por cambios que nadie hizo.
    this.initial = this.getValues();

    this._showStep(0);
  }

  get isLastStep() {
    return this.current === this.steps.length - 1;
  }

  // ---------------------------------------------------------------------------
  // Construcción
  // ---------------------------------------------------------------------------

  _build() {
    this.form = el('form', 'company-form');
    this.form.noValidate = true; // la validación la muestra el theme, no el navegador
    this.container.innerHTML = '';
    this.container.appendChild(this.form);

    if (this.steps.length > 1) this._buildTabs();

    this.stepNodes = this.steps.map((step, index) => this._buildStep(step, index));
    this.stepNodes.forEach((node) => this.form.appendChild(node));

    this._buildActions();

    this.form.addEventListener('submit', (event) => {
      event.preventDefault();
      this._submit();
    });

    // Cualquier cambio puede activar o desactivar un campo condicional. Se
    // escucha en el formulario entero porque el theme no sabe de qué campo
    // depende cuál: eso lo dice el esquema.
    this.form.addEventListener('input', () => this._applyConditions());
    this.form.addEventListener('change', () => this._applyConditions());

    this._enhance();
    this._applyConditions();
  }

  /**
   * Arranca los componentes del theme sobre el markup recién creado. Va aquí y
   * no en quien llama: el constructor sabe lo que acaba de pintar, y olvidar
   * este paso deja los campos sin label flotante ni formato de moneda.
   */
  _enhance() {
    // Un `<script>` al final del body corre ANTES de `DOMContentLoaded`, que es
    // cuando arranca el theme. Si el formulario se crea ahí, todavía no hay
    // componentes de MDB que aplicar: se espera y se reintenta, en vez de dejar
    // los campos a medio montar.
    if (!mdbComponents) {
      document.addEventListener('DOMContentLoaded', () => this._enhance(), { once: true });
      return;
    }

    upgradeFloatingLabels(this.form);
    pinNativeDateLabels(this.form);
    setupPickers(this.form, mdbComponents);

    // No se usa `initMDB()`: en la segunda llamada no hace nada (ver mdb-init.js),
    // y un formulario en modo edición se quedaría con los campos en crudo.
    initMdbOn(this.form, mdbComponents);
    setupRemoteSelects(this.form, mdbComponents);

    setupMonthPickers(this.form);
    setupPhoneInputs(this.form);
    setupCurrencyInputs(this.form);
    setupCardInputs(this.form);
    setupFileInputs(this.form);
  }

  _buildTabs() {
    this.tabs = el('div', 'form-steps');

    this.steps.forEach((step, index) => {
      const tab = el('button', 'form-step-tab');
      tab.type = 'button';
      tab.appendChild(el('span', 'form-step-number', String(index + 1)));
      tab.appendChild(el('span', null, step.title || `${formES.step} ${index + 1}`));

      // Solo se puede volver a un paso ya visitado: saltar a uno futuro se
      // saltaría también su validación.
      tab.addEventListener('click', () => {
        if (this.visited.has(index)) this.goToStep(index);
      });

      this.tabs.appendChild(tab);
    });

    this.form.appendChild(this.tabs);
  }

  _buildStep(step, index) {
    const node = el('section', 'form-step');
    node.dataset.step = String(index);

    if (step.title && this.steps.length === 1) {
      node.appendChild(el('h2', 'form-step-title', step.title));
    }

    const grid = el('div', 'form-grid');
    grid.style.setProperty('--form-columns', String(step.columns || 2));

    (step.fields || []).forEach((field) => grid.appendChild(this._buildField(field)));

    node.appendChild(grid);
    return node;
  }

  /**
   * Cada tipo se apoya en el componente del theme que ya existe, en vez de
   * reimplementar el control: el campo de moneda, el de teléfono, el selector
   * de mes y los de fecha son los mismos que fuera del formulario.
   */
  // ---------------------------------------------------------------------------
  // Campos que dependen de otro
  // ---------------------------------------------------------------------------

  /**
   * Si un campo debe verse, según `showIf`.
   *
   *   { name: 'razon_social', showIf: { tipo: 'empresa' } }
   *   { name: 'nombre',       showIf: { tipo: ['persona', 'natural'] } }
   *
   * Hace falta para formularios que cambian de forma: una factura a nombre de
   * una empresa pide razón social y NIT; a nombre de una persona, nombre,
   * apellido y cédula. Son el mismo formulario con dos caras, no dos
   * formularios.
   */
  _isVisible(field) {
    if (!field.showIf) return true;

    return Object.keys(field.showIf).every((name) => {
      const esperado = field.showIf[name];
      const actual = this._rawValue(name);

      return Array.isArray(esperado) ? esperado.indexOf(actual) > -1 : actual === esperado;
    });
  }

  /** Valor actual de un campo por nombre, sin pasar por `getValues()`. */
  _rawValue(name) {
    for (let i = 0; i < this.stepNodes.length; i += 1) {
      const wrapper = this.stepNodes[i].querySelector(`[data-field="${name}"]`);
      if (!wrapper) continue;

      const control = controlOf(wrapper);
      if (!control) return undefined;

      return control.type === 'checkbox' ? control.checked : control.value;
    }

    return undefined;
  }

  /**
   * Muestra u oculta los campos condicionales. Se llama en cada cambio del
   * formulario: cuál sea el campo del que dependen no lo sabe el theme.
   */
  _applyConditions() {
    let cambio = false;

    this.steps.forEach((step, index) => {
      (step.fields || []).forEach((field) => {
        if (!field.showIf) return;

        const wrapper = this.stepNodes[index].querySelector(`[data-field="${field.name}"]`);
        if (!wrapper) return;

        const visible = this._isVisible(field);
        if (wrapper.hidden === !visible) return;

        wrapper.hidden = !visible;
        cambio = true;

        // Un campo oculto no puede bloquear el envío ni arrastrar un error de
        // cuando sí se veía: el usuario no tendría forma de corregirlo.
        if (!visible) {
          wrapper.classList.remove('is-invalid');
          const error = wrapper.querySelector('.form-error');
          if (error) error.textContent = '';
        }
      });
    });

    if (cambio) {
      this.container.dispatchEvent(
        new CustomEvent('conditionsChanged.company.form', { bubbles: true })
      );
    }
  }

  _buildField(field) {
    const wrapper = el('div', `form-field${field.wide ? ' form-field-wide' : ''}`);
    wrapper.dataset.field = field.name;

    const id = `f${this.uid}-${field.name}`;
    const type = field.type || 'text';

    if (type === 'checkbox' || type === 'switch') {
      wrapper.appendChild(this._buildCheck(field, id, type));
    } else {
      wrapper.appendChild(this._buildControl(field, id, type));
    }

    if (field.help) wrapper.appendChild(el('small', 'form-help', field.help));

    const error = el('p', 'form-error');
    error.id = `${id}-error`;
    wrapper.appendChild(error);

    return wrapper;
  }

  /**
   * Campo de archivo. `accept` filtra el diálogo del sistema y `maxSize` avisa
   * pronto del peso; el límite de verdad lo pone el servidor.
   */
  _buildFile(field, id) {
    const box = el('div', 'company-file');
    box.setAttribute('data-company-file-init', '');
    if (field.accept) box.setAttribute('data-company-file-accept', field.accept);
    if (field.maxSize) box.setAttribute('data-company-file-max', field.maxSize);

    const control = el('input');
    control.type = 'file';
    control.id = id;
    control.name = field.name;
    // La clase la pone el propio componente; aquí no hace falta.
    if (field.required) control.required = true;

    const label = el('label', 'form-label company-file-label');
    label.htmlFor = id;
    label.textContent = field.label || field.name;
    if (field.required) label.appendChild(el('span', 'form-required', '*'));

    box.appendChild(label);
    box.appendChild(control);

    return box;
  }

  _buildControl(field, id, type) {
    // El campo de archivo tiene su propio control encima del input nativo, así
    // que no pasa por `.form-outline` ni por el label flotante de MDB.
    if (type === 'file') return this._buildFile(field, id);

    // Los `<select>` NO van dentro de un `.form-outline` con
    // `data-mdb-input-init`: el componente Input de MDB busca un `input` o un
    // `textarea` dentro y revienta con un select. MDB los trata aparte, y de
    // eso ya se encarga `upgradeFloatingLabels`, que convierte este markup
    // plano —select + label hermanos— al que MDB espera.
    const outline = el('div', type === 'select' ? null : 'form-outline');
    if (type !== 'select') outline.setAttribute('data-mdb-input-init', '');

    let control;

    if (type === 'textarea') {
      control = el('textarea');
      control.rows = field.rows || 3;
    } else if (type === 'select') {
      control = el('select');
      control.appendChild(el('option', null, ''));
      (field.options || []).forEach((option) => {
        const node = el('option', null, option.label);
        node.value = option.value;

        // Se ve pero no se elige. Esconderla haría pensar que el registro se
        // perdió; el motivo va aparte, no dentro del nombre.
        if (option.disabled) node.disabled = true;
        if (option.meta) node.setAttribute('data-mdb-secondary-text', option.meta);

        control.appendChild(node);
      });

      // El buscador viene encendido en todos los desplegables del theme.
      // `filter: false` en el campo lo apaga, para listas de dos opciones.
      if (field.filter === false) control.setAttribute('data-mdb-filter', 'false');

      // Con `url` el desplegable deja de vivir del HTML y pide páginas al
      // servidor. Es lo que hace falta cuando el catálogo tiene miles de filas.
      if (field.url) {
        control.setAttribute('data-company-remote-url', field.url);
        if (field.pageSize) control.setAttribute('data-company-remote-page-size', field.pageSize);
        if (field.minChars) control.setAttribute('data-company-remote-min-chars', field.minChars);
        if (field.valueField) control.setAttribute('data-company-remote-value', field.valueField);
        if (field.labelField) control.setAttribute('data-company-remote-label', field.labelField);
        if (field.disabledField) control.setAttribute('data-company-remote-disabled', field.disabledField);
        if (field.metaField) control.setAttribute('data-company-remote-meta', field.metaField);

        // `dependsOn` encadena catálogos: País → Departamento → Ciudad. El
        // valor del campo del que depende viaja como parámetro, y al cambiar
        // aquel se vacía este y se vuelve a consultar. Sin eso quedarían
        // ciudades de un departamento que ya no es el elegido.
        if (field.dependsOn) {
          control.setAttribute('data-company-remote-depends-on', field.dependsOn);
        }

        // En edición hace falta la opción YA elegida: el catálogo está vacío
        // hasta que se abre el desplegable, así que un `setValues({pais:'CO'})`
        // no encontraría esa opción y el campo se vería en blanco con el dato
        // guardado. Se pinta de entrada, igual que haría el servidor en Twig.
        if (field.selected && field.selected.value != null) {
          const elegida = el('option', null, field.selected.label);
          elegida.value = field.selected.value;
          elegida.selected = true;
          control.appendChild(elegida);
        }
      }
    } else {
      control = el('input');
      control.type = TEXT_LIKE.indexOf(type) > -1 ? type : 'text';
    }

    control.className = type === 'select' ? 'form-select' : 'form-control';
    control.id = id;
    control.name = field.name;
    if (field.placeholder) control.placeholder = field.placeholder;
    if (field.required) control.required = true;

    // Unidad pegada al campo: «%», «kg», «m²». Va como adorno del contenedor y
    // no dentro del valor, para que lo que se envía siga siendo un número
    // limpio y el backend no tenga que limpiar sufijos.
    if (field.suffix) {
      outline.classList.add('company-suffixed');
      outline.style.setProperty('--company-suffix', `'${field.suffix}'`);
    }

    // Tipos que activan un componente del theme.
    if (type === 'currency') {
      outline.setAttribute('data-company-currency-init', '');
      outline.setAttribute('data-company-currency', field.currency || 'COP');
      if (field.symbol !== false) outline.setAttribute('data-company-currency-symbol', '');
    } else if (type === 'card') {
      outline.setAttribute('data-company-card-init', '');
    } else if (type === 'phone') {
      outline.setAttribute('data-company-phone-init', '');
      if (field.country) outline.setAttribute('data-company-phone-country', field.country);
    } else if (type === 'month') {
      outline.setAttribute('data-company-month-init', '');
      if (field.mode) outline.setAttribute('data-company-month-mode', field.mode);
    } else if (type === 'date') {
      outline.classList.add('datepicker');
      outline.setAttribute('data-mdb-datepicker-init', '');
      if (field.format) outline.setAttribute('data-mdb-format', field.format);
    } else if (type === 'time') {
      outline.classList.add('timepicker');
      outline.setAttribute('data-mdb-timepicker-init', '');
    } else if (type === 'datetime') {
      outline.classList.add('datetimepicker');
      outline.setAttribute('data-mdb-datetimepicker-init', '');
    }

    const label = el('label', 'form-label');
    label.htmlFor = id;
    label.textContent = field.label || field.name;
    if (field.required) label.appendChild(el('span', 'form-required', '*'));

    outline.appendChild(control);
    outline.appendChild(label);

    return outline;
  }

  _buildCheck(field, id, type) {
    const box = el('div', `form-check${type === 'switch' ? ' form-switch' : ''}`);

    const input = el('input', 'form-check-input');
    input.type = 'checkbox';
    input.id = id;
    input.name = field.name;
    if (type === 'switch') input.setAttribute('role', 'switch');

    const label = el('label', 'form-check-label', field.label || field.name);
    label.htmlFor = id;

    box.appendChild(input);
    box.appendChild(label);
    return box;
  }

  _buildActions() {
    this.actions = el('div', 'form-actions');

    this.backButton = el('button', 'btn btn-link form-actions-back', formES.back);
    this.backButton.type = 'button';
    this.backButton.addEventListener('click', () => this.goToStep(this.current - 1));

    this.cancelButton = el('button', 'btn btn-outline-primary', this.options.cancelText || formES.cancel);
    this.cancelButton.type = 'button';
    this.cancelButton.addEventListener('click', () => this.cancel());

    this.submitButton = el('button', 'btn btn-primary');
    this.submitButton.type = 'submit';
    // El rótulo va en su propio nodo. Escribir `textContent` sobre el botón
    // borraría la rueda de espera al cambiar de paso, y el botón se quedaría
    // deshabilitado sin señal de que está haciendo algo.
    this.submitLabel = el('span', null, this.options.submitText || formES.save);
    this.submitButton.appendChild(this.submitLabel);

    this.actions.appendChild(this.backButton);
    this.actions.appendChild(this.cancelButton);
    this.actions.appendChild(this.submitButton);
    this.form.appendChild(this.actions);
  }

  // ---------------------------------------------------------------------------
  // Navegación
  // ---------------------------------------------------------------------------

  _showStep(index) {
    this.current = index;
    this.visited.add(index);

    this.stepNodes.forEach((node, i) => node.classList.toggle('is-active', i === index));

    // El paso acaba de hacerse visible: hay que volver a medirlo. Los campos se
    // inicializaron con el paso oculto —todos, incluido el primero, porque
    // `_showStep(0)` corre después de `_enhance()`—, y con `clientWidth` a cero
    // la muesca del borde queda en 8px. Solo se nota cuando el label flota, es
    // decir, al cargar valores para editar: el borde cruza el texto del label.
    refreshMdbOn(this.stepNodes[index], mdbComponents || {});

    if (this.tabs) {
      Array.prototype.forEach.call(this.tabs.children, (tab, i) => {
        tab.classList.toggle('is-active', i === index);
        tab.classList.toggle('is-done', i < index && this.visited.has(i));
        tab.dataset.visited = String(this.visited.has(i));
      });
    }

    this.backButton.hidden = index === 0;
    if (!this.busy) {
      this.submitLabel.textContent = this.isLastStep
        ? this.options.submitText || formES.save
        : formES.next;
    }

    this.container.dispatchEvent(
      new CustomEvent('stepChanged.company.form', {
        bubbles: true,
        detail: { step: index, total: this.steps.length },
      })
    );
  }

  /**
   * @param {number} index
   */
  goToStep(index) {
    if (index < 0 || index >= this.steps.length) return;

    // Hacia adelante hay que validar; hacia atrás no, para no bloquear a quien
    // solo quiere revisar lo que puso antes.
    if (index > this.current && !this.validate(this.current)) return;

    this._showStep(index);
  }

  _submit() {
    // Guardando ya: un segundo clic —o un Enter en un campo— mandaría el
    // formulario dos veces y crearía dos registros. El botón se pone
    // `disabled`, pero el Enter no pasa por él.
    if (this.busy) return;

    if (!this.isLastStep) {
      this.goToStep(this.current + 1);
      return;
    }

    // En el último paso se validan TODOS: alguien pudo volver atrás y borrar
    // un campo que ya estaba bien.
    for (let i = 0; i < this.steps.length; i += 1) {
      if (!this.validate(i)) {
        this._showStep(i);
        return;
      }
    }

    this.setBusy(true);

    this.container.dispatchEvent(
      new CustomEvent('submit.company.form', {
        bubbles: true,
        detail: {
          values: this.getValues(),
          // Los dos finales posibles, servidos aquí mismo para no obligar a
          // guardarse la referencia del formulario en un `onclick`.
          done: (options) => this.finish(options),
          fail: () => this.setBusy(false),
        },
      })
    );
  }

  // ---------------------------------------------------------------------------
  // Guardar: esperar sin dejar pulsar dos veces
  // ---------------------------------------------------------------------------

  /**
   * Pone —o quita— el estado de espera del botón de guardar.
   *
   * Sin esto, guardar es un clic que aparentemente no hace nada durante el
   * segundo que tarda el servidor, y lo natural es volver a pulsarlo: dos
   * peticiones y dos registros. El botón se deshabilita Y enseña una rueda,
   * porque un botón apagado y quieto se lee como «esto no funciona», no como
   * «estoy en ello».
   *
   * También se bloquea Cancelar: irse a mitad de un guardado deja al usuario
   * creyendo que no se guardó nada cuando el servidor ya lo tiene.
   *
   * @param {boolean} on
   */
  setBusy(on) {
    this.busy = !!on;

    this.submitButton.disabled = this.busy;
    this.submitButton.setAttribute('aria-busy', this.busy ? 'true' : 'false');
    this.cancelButton.disabled = this.busy;
    if (this.backButton) this.backButton.disabled = this.busy;

    if (!this.busy) {
      if (this.spinner) {
        this.spinner.remove();
        this.spinner = null;
      }
      // El texto vuelve a decidirlo el paso: en uno intermedio pone «Continuar».
      this.submitLabel.textContent = this.isLastStep
        ? this.options.submitText || formES.save
        : formES.next;
      return;
    }

    if (!this.spinner) {
      this.spinner = el('span', 'spinner-border spinner-border-sm company-btn-spinner');
      this.spinner.setAttribute('role', 'status');
      this.spinner.setAttribute('aria-hidden', 'true');
      this.submitButton.insertBefore(this.spinner, this.submitButton.firstChild);
    }

    // El rótulo cambia además de la rueda: dice QUÉ está pasando, y es lo que
    // lee quien va con lector de pantalla, que no ve girar nada.
    this.submitLabel.textContent = this.options.savingText || formES.saving;
  }

  /**
   * Se guardó. Devuelve el botón a su sitio y deja el formulario limpio, listo
   * para volver a usarse sin recargar la página.
   *
   * Es la mitad del contrato que hace falta cuando la aplicación NO recarga:
   * guarda, vuelve al listado y reutiliza el mismo formulario para el siguiente
   * registro. Si en cambio se redirige o se recarga, no hay que llamar a nada:
   * la página nueva trae el botón como estaba.
   *
   * @param {Object} [options]
   * @param {boolean} [options.reset=true] `false` para conservar lo escrito.
   */
  finish(options = {}) {
    this.setBusy(false);
    if (options.reset !== false) this.reset();
  }

  // ---------------------------------------------------------------------------
  // Cancelar y limpiar
  // ---------------------------------------------------------------------------

  /**
   * ¿Hay algo que perder?
   *
   * Se compara contra los valores con los que arrancó, no contra el vacío: en
   * modo edición el formulario nace lleno, y cualquier otra cuenta diría que
   * está tocado desde el primer momento.
   *
   * @returns {boolean}
   */
  isDirty() {
    const ahora = this.getValues();
    const nombres = Object.keys(ahora);

    for (let i = 0; i < nombres.length; i += 1) {
      const name = nombres[i];
      const antes = this.initial[name];
      const valor = ahora[name];

      // Comparación laxa a propósito: un campo vacío vale '' unas veces y
      // `undefined` otras según de dónde venga, y eso no es haber escrito nada.
      if ((antes == null ? '' : String(antes)) !== (valor == null ? '' : String(valor))) {
        return true;
      }
    }

    // Los ficheros no salen en `getValues()`, así que se miran aparte.
    return this._fileInputs().some((file) => !!file.file);
  }

  _fileInputs() {
    return Array.prototype.map
      .call(this.form.querySelectorAll('[data-field]'), (wrapper) =>
        componentIn(wrapper, '__companyFile')
      )
      .filter(Boolean);
  }

  /**
   * Cancelar: se pregunta, se limpia y se avisa.
   *
   * Cancelar es destructivo aunque no lo parezca —se pierde todo lo escrito— y
   * el botón está pegado al de guardar, así que se pulsa por error. De ahí la
   * confirmación.
   *
   * **Pero solo si hay algo que perder.** Preguntar «se perderá lo que llevas
   * escrito» a quien abrió el formulario y se arrepintió sin escribir nada es
   * mentira y estorbo, y a base de repetirse enseña a confirmar sin leer, que
   * es justo lo que hace peligroso el día que sí importa. Con
   * `confirmCancel: 'always'` se pregunta siempre, y con `false` nunca.
   *
   * Quien cancela ya decidió irse: el botón del diálogo NO es rojo. El rojo se
   * reserva para lo que destruye algo que ya existía.
   *
   * @returns {Promise<boolean>} Si se canceló de verdad.
   */
  cancel() {
    // A mitad de un guardado no se cancela: la petición ya salió y el registro
    // puede estar creándose.
    if (this.busy) return Promise.resolve(false);

    const modo = this.options.confirmCancel === undefined ? 'dirty' : this.options.confirmCancel;
    const preguntar = modo === 'always' || (modo !== false && this.isDirty());

    if (!preguntar) {
      this._doCancel();
      return Promise.resolve(true);
    }

    return confirm({
      type: 'warning',
      title: this.options.cancelTitle || formES.cancelTitle,
      message: this.options.cancelMessage || formES.cancelMessage,
      confirmText: this.options.cancelConfirm || formES.cancelConfirm,
      cancelText: this.options.cancelKeep || formES.cancelKeep,
    }).then((ok) => {
      if (ok) this._doCancel();
      return ok;
    });
  }

  _doCancel() {
    this.reset();

    // El theme no sabe a dónde se vuelve: eso es de la aplicación. Si le pasan
    // un `pageNav`, lo hace él; si no, avisa y que decida quien escucha.
    this.container.dispatchEvent(
      new CustomEvent('cancel.company.form', { bubbles: true, detail: {} })
    );

    if (this.options.nav && typeof this.options.nav.back === 'function') {
      this.options.nav.back();
    }
  }

  /**
   * Deja el formulario como recién abierto: sin valores, sin errores y en el
   * primer paso.
   *
   * Vuelve a los valores DE PARTIDA, no al vacío. En edición eso es la ficha
   * original: quien pulsa cancelar espera deshacer sus cambios, no vaciar el
   * registro.
   *
   * Los pasos visitados también se olvidan, o al reabrirlo se podría saltar
   * directamente al último sin pasar por las validaciones del camino.
   */
  reset() {
    const vacio = {};
    this.steps.forEach((step) => {
      (step.fields || []).forEach((field) => {
        vacio[field.name] =
          this.initial[field.name] === undefined ? '' : this.initial[field.name];
      });
    });

    this.setValues(vacio);
    this._fileInputs().forEach((file) => file.clear());

    // Los errores se quitan a mano: son del theme, no de los controles, así que
    // no se van solos al reescribir los valores.
    this.form.querySelectorAll('[data-field]').forEach((wrapper) => {
      wrapper.classList.remove('is-invalid');
      const error = wrapper.querySelector('.form-error');
      if (error) error.textContent = '';
    });

    this.visited = new Set([0]);
    this._showStep(0);
  }

  // ---------------------------------------------------------------------------
  // Validación
  // ---------------------------------------------------------------------------

  /**
   * @param {number} [step] Paso a validar; sin argumento, todos.
   * @returns {boolean}
   */
  validate(step) {
    const nodos = step == null ? this.stepNodes : [this.stepNodes[step]];
    let primerError = null;

    nodos.forEach((node, i) => {
      const indice = step == null ? i : step;

      (this.steps[indice].fields || []).forEach((field) => {
        const wrapper = node.querySelector(`[data-field="${field.name}"]`);

        // Un campo que no se ve no se valida: exigir algo que el usuario no
        // tiene delante deja el formulario bloqueado sin nada que corregir.
        if (wrapper.hidden) return;

        const control = controlOf(wrapper);
        const mensaje = this._errorFor(field, control);

        wrapper.classList.toggle('is-invalid', !!mensaje);
        control.setAttribute('aria-invalid', mensaje ? 'true' : 'false');
        wrapper.querySelector('.form-error').textContent = mensaje || '';

        if (mensaje && !primerError) primerError = { indice, control };
      });
    });

    if (primerError) {
      // Se lleva a la vista el primer campo con problema: si el error está en
      // otro paso, de nada sirve marcarlo sin mostrarlo.
      if (primerError.indice !== this.current) this._showStep(primerError.indice);
      primerError.control.focus();
      return false;
    }

    return true;
  }

  _errorFor(field, control) {
    const type = field.type || 'text';
    const value = type === 'checkbox' || type === 'switch' ? control.checked : control.value.trim();

    if (field.required && (value === '' || value === false)) return formES.required;
    if (value === '' || value === false) return null;

    if (type === 'email' && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) return formES.email;

    // La tarjeta la valida su propio componente: longitud según la marca y
    // dígito de control. Repetir esa lógica aquí sería tener dos verdades.
    if (type === 'card') {
      const card = control.closest('[data-company-card-init]');
      const instancia = card && card.__companyCard;
      if (instancia && !instancia.isValid()) return cardES.invalid;
    }

    if (type === 'number' || type === 'currency') {
      const numero = Number(String(value).replace(/[^\d.-]/g, ''));
      if (Number.isNaN(numero)) return formES.number;
      if (field.min != null && numero < field.min) return `${formES.min} ${field.min}`;
      if (field.max != null && numero > field.max) return `${formES.max} ${field.max}`;
    }
    if (field.minLength && value.length < field.minLength) {
      return `${formES.minLength} ${field.minLength}`;
    }
    if (field.pattern && !new RegExp(field.pattern).test(value)) return field.patternMessage || formES.invalid;

    return null;
  }

  // ---------------------------------------------------------------------------
  // Valores
  // ---------------------------------------------------------------------------

  /**
   * @returns {Object} Los valores, ya en crudo para los campos que lo tienen
   *   (moneda en `1000000.00`, teléfono en E.164).
   */
  getValues() {
    const values = {};

    this.steps.forEach((step, index) => {
      (step.fields || []).forEach((field) => {
        const wrapper = this.stepNodes[index].querySelector(`[data-field="${field.name}"]`);

        // Los campos ocultos no se envían: si se factura a una persona, la
        // razón social no debe viajar con el valor que quedó de antes.
        if (wrapper.hidden) return;

        const control = controlOf(wrapper);
        const type = field.type || 'text';

        if (type === 'checkbox' || type === 'switch') {
          values[field.name] = control.checked;
          return;
        }

        // Moneda y teléfono guardan su valor crudo en un input oculto que lleva
        // el `name`; el visible se queda con el formateado.
        const oculto = wrapper.querySelector(`input[type="hidden"][name="${field.name}"]`);
        values[field.name] = oculto ? oculto.value : control.value;
      });
    });

    return values;
  }

  /**
   * @param {Object} values
   */
  setValues(values = {}) {
    this.steps.forEach((step, index) => {
      (step.fields || []).forEach((field) => {
        if (!(field.name in values)) return;

        const wrapper = this.stepNodes[index].querySelector(`[data-field="${field.name}"]`);
        const control = controlOf(wrapper);
        const type = field.type || 'text';
        const value = values[field.name];

        if (type === 'checkbox' || type === 'switch') {
          control.checked = !!value;
          return;
        }

        // MDB esconde el `<select>` y pinta el suyo encima. Escribir en el
        // original no actualiza lo que se ve, así que hay que pasar por la
        // instancia: es lo que rompía el modo edición.
        const select = mdbComponents && mdbComponents.Select && control.tagName === 'SELECT'
          ? mdbComponents.Select.getInstance(control)
          : null;

        if (select) {
          select.setValue(value == null ? '' : value);

          // `setValue()` no emite el `change` nativo —solo su evento propio—,
          // así que nada de lo que dependa de este campo se enteraría. Un
          // formulario cargado en modo edición mostraría los campos
          // condicionales del valor anterior.
          control.dispatchEvent(new Event('change', { bubbles: true }));
          return;
        }

        // Los campos con componente propio se fijan por SU método, no
        // escribiendo el input visible.
        //
        // Ese input no guarda el valor de verdad: moneda enseña «$ 65.000» y
        // teléfono solo la parte nacional, mientras el valor crudo vive en un
        // input oculto. Escribir ahí el valor crudo equivale a teclearlo, así
        // que el componente lo vuelve a formatear sobre lo ya formateado: el
        // importe se multiplica y el prefijo del teléfono se duplica. Se veía al
        // cargar un registro para editar y guardarlo sin tocar nada.
        const propio =
          componentIn(wrapper, '__companyCurrency') || componentIn(wrapper, '__companyPhone');
        if (propio && typeof propio.setValue === 'function') {
          propio.setValue(value == null ? '' : value);
          return;
        }

        control.value = value == null ? '' : value;
        // Se avisa para que los componentes del theme reformateen y para que
        // MDB flote el label de un campo que llega con contenido.
        control.dispatchEvent(new Event('input', { bubbles: true }));
        control.dispatchEvent(new Event('change', { bubbles: true }));
      });
    });

    // Una pasada final: los valores se aplican campo a campo, y una condición
    // puede depender de uno que se fija DESPUÉS que el campo condicionado.
    this._applyConditions();
  }

  /** Filtra los pasos por texto; útil en formularios muy largos. */
  findField(text) {
    const term = normalizeText(text);
    return this.steps
      .flatMap((step) => step.fields || [])
      .filter((field) => normalizeText(field.label || field.name).includes(term));
  }

  dispose() {
    this.container.innerHTML = '';
    delete this.container.__companyForm;
  }
}

/**
 * Crea un formulario.
 *
 * @param {string|HTMLElement} container
 * @param {Object} options
 * @returns {Form}
 */
export function form(container, options) {
  const node = typeof container === 'string' ? document.querySelector(container) : container;
  const instance = new Form(node, options);
  node.__companyForm = instance;

  return instance;
}

form.getInstance = (container) => {
  const node = typeof container === 'string' ? document.querySelector(container) : container;
  return node ? node.__companyForm || null : null;
};

export { Form };
export default form;
